import * as Sentry from '@sentry/react';

// Safe Sentry state management
let sentryInitialized = false;
let sentryError: Error | null = null;

export function initSentry() {
  try {
    // ✅ Use Vite environment variables (import.meta.env)
    const dsn = import.meta.env.VITE_SENTRY_DSN || ''; 
    const environment = import.meta.env.MODE || 'production'; // 'development' | 'production'
    
    if (!dsn) {
      console.log('[Sentry] Running without error tracking - DSN not configured');
      console.log('[Sentry] Set VITE_SENTRY_DSN in .env to enable error tracking');
      sentryError = new Error('Sentry DSN not configured');
      return false;
    }

    // Validate DSN format
    if (!dsn.includes('sentry.io')) {
      console.error('[Sentry] Invalid DSN format provided');
      sentryError = new Error('Invalid Sentry DSN format');
      return false;
    }

    console.log(`[Sentry] Initializing with environment: ${environment}`);
    
    Sentry.init({
      dsn,
      environment,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
      replaysSessionSampleRate: environment === 'production' ? 0.1 : 1.0,
      replaysOnErrorSampleRate: 1.0,
      
      // Trace propagation to backend
      tracePropagationTargets: [
        'localhost',
        /^https:\/\/[^/]+\.supabase\.co/,
      ],
      beforeSend(event) {
        // Filter out common non-actionable errors
        if (event.exception) {
          const error = event.exception.values?.[0];
          const errorValue = error?.value?.toLowerCase() || '';
          
          // Filter ResizeObserver errors
          if (errorValue.includes('resizeobserver loop limit exceeded')) {
            return null;
          }
          
          // Filter non-error promise rejections
          if (errorValue.includes('non-error promise rejection captured')) {
            return null;
          }
          
          // Filter expected authentication cleanup errors
          if (
            errorValue.includes('refresh_token') ||
            errorValue.includes('token invalidated') ||
            errorValue.includes('jwt expired') ||
            errorValue.includes('bad jwt') ||
            errorValue.includes('invalid jwt') ||
            (errorValue.includes('400') && sessionStorage.getItem('signing_out') === 'true')
          ) {
            console.info('[Sentry] Filtered expected auth cleanup error:', errorValue);
            return null;
          }
          
          // Filter expected CORS errors on protected routes after logout
          if (
            errorValue.includes('cors') && 
            (errorValue.includes('dashboard') || sessionStorage.getItem('signing_out') === 'true')
          ) {
            console.info('[Sentry] Filtered expected CORS error (protected route after logout)');
            return null;
          }
          
          // Filter manifest access errors during auth redirects
          if (errorValue.includes('manifest')) {
            console.info('[Sentry] Filtered manifest access error (expected during redirects)');
            return null;
          }
        }
        return event;
      },
    });
    
    sentryInitialized = true;
    console.log(`[Sentry] Successfully initialized in ${environment} mode ✅`);
    return true;
  } catch (error) {
    sentryError = error as Error;
    console.error('[Sentry] Failed to initialize:', error);
    return false;
  }
}

// Safe Sentry wrapper functions
export function isSentryAvailable(): boolean {
  return sentryInitialized && !sentryError;
}

export function safeSentryCapture(method: 'captureException' | 'captureMessage', data: any, context?: any) {
  if (!isSentryAvailable()) {
    console.warn('[Sentry] Not available, logging to console:', { method, data, context });
    return;
  }
  
  try {
    if (method === 'captureException') {
      if (context) {
        Sentry.withScope((scope) => {
          Object.entries(context).forEach(([key, value]: [string, any]) => {
            if (key === 'tags') {
              Object.entries(value).forEach(([tagKey, tagValue]: [string, any]) => {
                scope.setTag(tagKey, tagValue);
              });
            } else if (key === 'level') {
              scope.setLevel(value);
            } else {
              scope.setExtra(key, value);
            }
          });
          Sentry.captureException(data);
        });
      } else {
        Sentry.captureException(data);
      }
    } else if (method === 'captureMessage') {
      if (context) {
        Sentry.withScope((scope) => {
          Object.entries(context).forEach(([key, value]: [string, any]) => {
            if (key === 'tags') {
              Object.entries(value).forEach(([tagKey, tagValue]: [string, any]) => {
                scope.setTag(tagKey, tagValue);
              });
            } else if (key === 'level') {
              scope.setLevel(value);
            } else {
              scope.setExtra(key, value);
            }
          });
          Sentry.captureMessage(data, context.level || 'info');
        });
      } else {
        Sentry.captureMessage(data);
      }
    }
  } catch (error) {
    console.error('[Sentry] Error while capturing:', error);
  }
}

/**
 * Set user context for error tracking
 * Call after user authentication
 */
export function setSentryUser(user: { id: string; email?: string }): void {
  if (!isSentryAvailable()) return;
  try {
    Sentry.setUser({
      id: user.id,
      email: user.email,
    });
  } catch (error) {
    console.error('[Sentry] Error setting user context:', error);
  }
}

/**
 * Clear user context (e.g., on logout)
 */
export function clearSentryUser(): void {
  if (!isSentryAvailable()) return;
  try {
    Sentry.setUser(null);
  } catch (error) {
    console.error('[Sentry] Error clearing user context:', error);
  }
}

/**
 * TEST ONLY: Trigger a test error to verify Sentry is working
 * Usage: In browser console, run: window.testSentry()
 */
export function testSentry(): void {
  try {
    console.log('[Sentry Test] Triggering test error...');
    throw new Error('Test error: Sentry integration verification');
  } catch (error) {
    if (isSentryAvailable()) {
      Sentry.captureException(error);
      console.log('[Sentry Test] Error sent to Sentry. Check your dashboard.');
    } else {
      console.warn('[Sentry Test] Sentry not available - error not captured');
    }
  }
}

// Make available in browser console for testing
if (import.meta.env.DEV) {
  (window as any).testSentry = testSentry;
}

export { Sentry };
